import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { ErrorBox, Loading, Notice } from '../../components/ui';
import { useHumanCheck } from '../../components/Turnstile';
import { usePageMeta } from '../../lib/meta';
import { api, ApiError, errorMessage } from '../../lib/api';
import { useSession } from '../../session';
import { AuthCard } from './Auth';

const validToken = (t: string | undefined): t is string => !!t && t.length >= 20 && t.length <= 200;

/** /forgot — request a reset link. The answer is the same whether or not the account exists. */
export function Forgot() {
  usePageMeta('Reset password', 'Get a link to choose a new Ayonix Studio password.');
  const { query } = useLocation();
  const [email, setEmail] = useState(query.email ?? '');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const human = useHumanCheck('forgot');

  const submit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    const addr = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) { setError('Enter the email address you signed up with.'); return; }
    if (human.required && !human.token) { setError(human.pendingMessage); return; }
    setBusy(true);
    try {
      const r = await api<{ ok: boolean; message: string }>('/v1/auth/forgot', { method: 'POST', body: { email: addr, client: 'web', turnstileToken: human.token ?? undefined } });
      setSent(r.message);
    } catch (err) {
      setError(errorMessage(err));
      human.reset();
    } finally { setBusy(false); }
  };

  return (
    <AuthCard title="Reset your password" lead={sent ? undefined : 'Enter your account email and we’ll send you a link to choose a new password.'}>
      {sent ? (
        <div class="stack" data-testid="forgot-sent">
          <Notice tone="success"><p>{sent}</p></Notice>
          <p class="small muted" style="margin:0">The link works for one hour. Check your spam folder if it doesn’t arrive.</p>
          <a class="btn block" href="/login">Back to sign in</a>
        </div>
      ) : (
        <>
          <form class="stack" onSubmit={submit} noValidate>
            <div class="field">
              <label for="forgot-email">Email</label>
              <input id="forgot-email" class="input" type="email" autoComplete="email" required value={email} onInput={(e) => setEmail(e.currentTarget.value)} />
            </div>
            {human.box}
            {error && <ErrorBox message={error} />}
            <button class="btn primary block" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send reset link'}</button>
          </form>
          <p class="small" style="margin:16px 0 0">Remembered it? <a href="/login">Sign in</a></p>
        </>
      )}
    </AuthCard>
  );
}

/** /reset?token= — choose a new password. Success signs out every session, then sends you to sign in. */
export function Reset() {
  usePageMeta('Choose a new password', 'Set a new password for your Ayonix Studio account.');
  const { query, route } = useLocation();
  const { refresh } = useSession();
  const token = query.token;
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<{ text: string; expired: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!validToken(token)) {
    return (
      <AuthCard title="Reset link not valid">
        <div class="stack">
          <ErrorBox message="This reset link is incomplete. Open the link from the email again, or request a new one." />
          <a class="btn primary block" href="/forgot">Request a new link</a>
        </div>
      </AuthCard>
    );
  }

  const submit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (password.length < 10) { setError({ text: 'Use at least 10 characters for your new password.', expired: false }); return; }
    if (password !== confirm) { setError({ text: 'The passwords don’t match.', expired: false }); return; }
    setBusy(true);
    try {
      await api('/v1/auth/reset', { method: 'POST', body: { token, password } });
      await refresh(); // every session was revoked, including this browser's
      route('/login?reset=1', true);
    } catch (err) {
      setError({ text: errorMessage(err), expired: err instanceof ApiError && err.code === 'invalid_token' });
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Choose a new password" lead="You’ll be signed out everywhere and can sign in again with the new password.">
      <form class="stack" onSubmit={submit} noValidate>
        <div class="field">
          <label for="new-pw">New password</label>
          <input id="new-pw" class="input" type="password" autoComplete="new-password" required minLength={10} aria-describedby="new-pw-hint" value={password} onInput={(e) => setPassword(e.currentTarget.value)} />
          <span id="new-pw-hint" class="hint">At least 10 characters.</span>
        </div>
        <div class="field">
          <label for="confirm-pw">Confirm new password</label>
          <input id="confirm-pw" class="input" type="password" autoComplete="new-password" required value={confirm} onInput={(e) => setConfirm(e.currentTarget.value)} />
        </div>
        {error && <ErrorBox message={error.text}>{error.expired && <p><a href="/forgot">Request a new link</a></p>}</ErrorBox>}
        <button class="btn primary block" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Set new password'}</button>
      </form>
    </AuthCard>
  );
}

/** /verify?token= — confirms the email address as soon as the page opens. */
export function Verify() {
  usePageMeta('Confirm email', 'Confirm the email address for your Ayonix Studio account.');
  const { query } = useLocation();
  const { refresh } = useSession();
  const token = query.token;
  const [state, setState] = useState<'working' | 'done' | 'invalid' | 'error'>(validToken(token) ? 'working' : 'invalid');
  const [message, setMessage] = useState<string>('This confirmation link is incomplete. Open the link from the email again.');
  const started = useRef(false);

  const run = async () => {
    setState('working');
    try {
      await api('/v1/auth/verify', { method: 'POST', body: { token } });
      setState('done');
      refresh();
    } catch (err) {
      setMessage(errorMessage(err));
      setState(err instanceof ApiError && err.status === 400 ? 'invalid' : 'error');
    }
  };
  useEffect(() => {
    if (started.current || !validToken(token)) return;
    started.current = true;
    run();
  }, []);

  const title = { working: 'Confirming your email…', done: 'Email confirmed', invalid: 'Link not valid', error: 'Couldn’t confirm your email' }[state];
  return (
    <AuthCard title={title}>
      {state === 'working' && <Loading label="Checking your link…" />}
      {state === 'done' && (
        <div class="stack" data-testid="verify-done">
          <Notice tone="success"><p>Thanks — your email address is confirmed.</p></Notice>
          <a class="btn primary block" href="/library">Go to your library</a>
        </div>
      )}
      {state === 'invalid' && (
        <div class="stack" data-testid="verify-invalid">
          <ErrorBox message={message} />
          <a class="btn primary block" href="/app/account">Resend from your account</a>
        </div>
      )}
      {state === 'error' && <ErrorBox message={message} onRetry={run} />}
    </AuthCard>
  );
}
